import React, { useMemo } from "react";
import Asset from "../Asset/Asset";
import { MenuItem, MenuItemCategory } from "../../state/types/menuTypes";
import { useMenuScrollSpy } from "../../hooks/useMenuScrollSpy";
import utils from "../../services/utils";

type Props = {
  categories: readonly MenuItemCategory[];
  groupedMenuItems: readonly MenuItem[];
  sectionRefs: Record<string, React.RefObject<HTMLDivElement>>;
};

/**
 * # ShortcutBar
 * * **PURPOSE:** * High-performance sticky navigation synchronizing horizontal category pills with vertical "Scroll Spy."
 * * ### ARCHITECTURE & PERFORMANCE STRATEGY:
 * * **1. Hook-Based Orchestration:** * Delegates complex browser API logic (RAF, Scroll Events, Visibility) to `useMenuScrollSpy`.
 * * **2. Direct DOM Mutation:** * Utilizes forwarded refs to allow the hook to mutate styles directly, bypassing the React render cycle.
 * * **3. Directional Visibility:** * Maximizes mobile real estate by toggling bar visibility based on scroll delta.
 * * **4. Purely Presentational:** * This component focuses strictly on data mapping and semantic HTML structure.
 * * **5. Metric Resilience:** * Handles dynamic layout changes via the hook's cached metric system.
 */
const ShortcutBar: React.FC<Props> = ({
  categories,
  groupedMenuItems,
  sectionRefs,
}) => {
  /** * DATA MAPPING:
   * Reconciles raw menu groups with category metadata (images, visibility flags).
   * Filtered to exclude categories explicitly marked as hidden in the CMS.
   */
  const menuShortcutData = useMemo(() => {
    return groupedMenuItems
      .map((group) => ({
        group,
        category: categories.find((c) => c.name === group.name),
      }))
      .filter(
        ({ category }) => utils.isDefined(category) && category.shortcutVisible
      );
  }, [categories, groupedMenuItems]);

  // Derived list of section IDs used by the Scroll Spy detection engine
  const activeShortcutNames = useMemo(
    () => menuShortcutData.map(({ group }) => group.name),
    [menuShortcutData]
  );

  /** * BROWSER ENGINE:
   * Connects this UI to the hardware-accelerated scroll logic.
   * Returns refs for manual DOM manipulation and the current 'active' state.
   */
  const {
    activeCategoryName,
    stickyBarWrapperRef,
    categoryNavScrollRef,
    categoryPillRefs,
    scrollToSection,
  } = useMenuScrollSpy({ activeShortcutNames, sectionRefs });

  return (
    <nav
      ref={stickyBarWrapperRef}
      className="category-sticky"
      aria-label="Menu category shortcuts"
    >
      <div ref={categoryNavScrollRef} className="category-nav-scroll">
        <ul className="category-nav">
          {menuShortcutData.map(({ group, category }) => {
            const isActive = activeCategoryName === group.name;
            const assetId = category?.displayAssetId;
            return (
              <li key={group.name} className="category-nav-item">
                {/* Ref bridge: Allows the hook to track the horizontal position of each pill */}
                <div ref={(el) => (categoryPillRefs.current[group.name] = el)}>
                  <button
                    type="button"
                    className={`category-pill ${isActive ? "is-active" : ""}`}
                    aria-current={isActive ? "location" : undefined}
                    onClick={() => scrollToSection(group.name)}
                  >
                    {utils.isDefined(assetId) && (
                      <div className="thumbnail-image" aria-hidden="true">
                        <Asset thumbnailAssetId={assetId} transform="100x" />
                      </div>
                    )}
                    <span className="category-name">{group.name}</span>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};

export default ShortcutBar;
